import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { LuWandSparkles } from "react-icons/lu";
import {
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  previewTemplate,
  mergePreviewTemplate,
  getDocumentDesigns,
} from "../../../services/companyAdminService";
import { useApp } from "../../../context/AppContext";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Stack,
} from "@/components/ui";
import AppLoading from "@/components/AppLoading";
import { PlusCircleIcon } from "@/components/icons";
import TemplateRichEditor from "@/components/TemplateRichEditor";
import DocumentDesignThumbnail from "@/components/DocumentDesignThumbnail/DocumentDesignThumbnail";
import AiGenerateWizard from "./AiGenerateWizard";

const pal = (id, nameHe, nameEn, primary, secondary, accent) => ({
  id,
  nameHe,
  nameEn,
  primary,
  secondary,
  accent,
  headerBg: primary,
  textOnHeader: "#ffffff",
});

const COLOR_PALETTE_CATEGORIES = [
  {
    id: "classic",
    nameHe: "קלאסי",
    nameEn: "Classic",
    palettes: [
      pal("classic-navy", "צי קלאסי", "Classic navy", "#1a237e", "#0d47a1", "#3949ab"),
      pal("classic-burgundy", "בורדו", "Burgundy", "#6d1b1b", "#4e1515", "#c62828"),
      pal("classic-charcoal", "פחם", "Charcoal", "#37474f", "#263238", "#607d8b"),
      pal("classic-royal", "כחול מלכותי", "Royal blue", "#1565c0", "#0d47a1", "#42a5f5"),
      pal("classic-forest", "יער כהה", "Forest", "#1b5e20", "#2e7d32", "#66bb6a"),
      pal("classic-slate", "אפור צפחה", "Slate", "#455a64", "#263238", "#78909c"),
      pal("classic-wine", "יין", "Wine", "#6d1b1b", "#4e1515", "#b71c1c"),
      pal("classic-ink", "דיו", "Ink", "#0d47a1", "#01579b", "#1976d2"),
      pal("classic-midnight", "חצות", "Midnight", "#0d1b2a", "#1b263b", "#415a77"),
      pal("classic-olive", "זית כהה", "Dark olive", "#3e2723", "#5d4037", "#8d6e63"),
      pal("classic-maroon", "ארגמן כהה", "Maroon", "#880e4f", "#ad1457", "#e91e63"),
      pal("classic-graphite", "גרפיט", "Graphite", "#263238", "#37474f", "#546e7a"),
    ],
  },
  {
    id: "modern",
    nameHe: "מודרני",
    nameEn: "Modern",
    palettes: [
      pal("modern-mist", "ערפל", "Mist", "#90a4ae", "#607d8b", "#b0bec5"),
      pal("modern-lavender", "לבנדר", "Lavender", "#7e57c2", "#5e35b1", "#b39ddb"),
      pal("modern-peach", "אפרסק", "Peach", "#ff8a65", "#e64a19", "#ffab91"),
      pal("modern-mint", "מנטה רך", "Soft mint", "#4db6ac", "#00897b", "#80cbc4"),
      pal("modern-sky", "שמיים בהיר", "Light sky", "#03a9f4", "#0288d1", "#4fc3f7"),
      pal("modern-dust", "אבק", "Dust", "#a1887f", "#8d6e63", "#bcaaa4"),
      pal("modern-blush", "סומק", "Blush", "#f48fb1", "#ec407a", "#f8bbd9"),
      pal("modern-sage", "מרווה", "Sage", "#8bc34a", "#689f38", "#aed581"),
      pal("modern-steel", "פלדה מודרנית", "Steel", "#546e7a", "#37474f", "#90a4ae"),
      pal("modern-coral", "אלמוגים רכים", "Soft coral", "#ff7043", "#e64a19", "#ffab91"),
      pal("modern-cyan", "ציאן", "Cyan", "#00acc1", "#0097a7", "#26c6da"),
      pal("modern-indigo", "אינדיגו רך", "Soft indigo", "#5c6bc0", "#3f51b5", "#9fa8da"),
      pal("modern-sand", "חול", "Sand", "#d7ccc8", "#bcaaa4", "#efebe9"),
      pal("modern-rose", "ורוד אפרורי", "Dusty rose", "#e91e63", "#ad1457", "#f06292"),
      pal("modern-ocean", "אוקיינוס מודרני", "Modern ocean", "#0288d1", "#01579b", "#4fc3f7"),
    ],
  },
  {
    id: "nature",
    nameHe: "טבע",
    nameEn: "Nature",
    palettes: [
      pal("nature-ocean", "אוקיינוס", "Ocean", "#006064", "#00838f", "#26c6da"),
      pal("nature-forest", "יער", "Forest", "#2e7d32", "#1b5e20", "#66bb6a"),
      pal("nature-sunset", "שקיעה", "Sunset", "#e65100", "#bf360c", "#ff9800"),
      pal("nature-sage", "מרווה ירוקה", "Sage green", "#558b2f", "#33691e", "#7cb342"),
      pal("nature-terracotta", "טרקוטה", "Terracotta", "#bf360c", "#8d6e63", "#d84315"),
      pal("nature-earth", "אדמה", "Earth", "#5d4037", "#3e2723", "#8d6e63"),
      pal("nature-moss", "טחב", "Moss", "#33691e", "#558b2f", "#689f38"),
      pal("nature-lake", "אגם", "Lake", "#0277bd", "#01579b", "#03a9f4"),
      pal("nature-autumn", "סתיו", "Autumn", "#ef6c00", "#e65100", "#ff9800"),
      pal("nature-meadow", "אחו", "Meadow", "#43a047", "#2e7d32", "#66bb6a"),
      pal("nature-storm", "סערה", "Storm", "#455a64", "#37474f", "#78909c"),
      pal("nature-amber", "ענבר", "Amber", "#ff8f00", "#e65100", "#ffb300"),
      pal("nature-jade", "יהלם", "Jade", "#00796b", "#004d40", "#4db6ac"),
      pal("nature-clay", "חימר", "Clay", "#8d6e63", "#6d4c41", "#a1887f"),
      pal("nature-aurora", "זוהר צפוני", "Aurora", "#00695c", "#004d40", "#26a69a"),
    ],
  },
  {
    id: "bold",
    nameHe: "נועז",
    nameEn: "Bold",
    palettes: [
      pal("bold-electric", "חשמלי", "Electric", "#00bcd4", "#0097a7", "#4dd0e1"),
      pal("bold-neon-pink", "ורוד ניאון", "Neon pink", "#e91e63", "#c2185b", "#f06292"),
      pal("bold-deep-purple", "סגול עמוק", "Deep purple", "#4527a0", "#311b92", "#7e57c2"),
      pal("bold-crimson", "ארגמן", "Crimson", "#ad1457", "#880e4f", "#ec407a"),
      pal("bold-emerald", "אמרלד", "Emerald", "#00796b", "#004d40", "#26a69a"),
      pal("bold-coral", "אלמוגים", "Coral", "#ff5722", "#e64a19", "#ff8a65"),
      pal("bold-magenta", "מג'נטה", "Magenta", "#ad1457", "#6a1b9a", "#e91e63"),
      pal("bold-cyan", "ציאן בולט", "Bold cyan", "#00bcd4", "#009688", "#4dd0e1"),
      pal("bold-orange", "כתום בולט", "Bold orange", "#ff6f00", "#e65100", "#ffab40"),
      pal("bold-teal", "טורקיז בולט", "Bold teal", "#00897b", "#00695c", "#26a69a"),
      pal("bold-violet", "סגול ויולט", "Violet", "#7b1fa2", "#4a148c", "#ab47bc"),
      pal("bold-ruby", "רובי", "Ruby", "#b71c1c", "#8b0000", "#e53935"),
      pal("bold-gold", "זהב", "Gold", "#ffa000", "#ff8f00", "#ffca28"),
      pal("bold-lime", "ליים נועז", "Bold lime", "#7cb342", "#558b2f", "#9ccc65"),
      pal("bold-indigo", "אינדיגו", "Indigo", "#303f9f", "#1a237e", "#5c6bc0"),
    ],
  },
];

const COLOR_PALETTES = COLOR_PALETTE_CATEGORIES.flatMap((c) => c.palettes);

/** סדר ותוויות לקטגוריות עימוד מסמך (מתאים ל־category מהשרת) */
const DESIGN_LAYOUT_CATEGORIES = [
  { id: "minimal", nameHe: "מינימלי", nameEn: "Minimal" },
  { id: "modern", nameHe: "מודרני", nameEn: "Modern" },
  { id: "corporate", nameHe: "עסקי", nameEn: "Corporate" },
  { id: "classic", nameHe: "קלאסי", nameEn: "Classic" },
  { id: "invoice", nameHe: "חשבוניות", nameEn: "Invoices" },
];

const DEFAULT_HTML = `<div class="contract-content" dir="rtl">
  <h1 style="text-align: center; color: #2c5282;">הסכם שירות</h1>
  <p style="text-align: center; color: #666;">שם החברה שלך</p>
  <div style="background: #f7fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3>פרטי ההסכם</h3>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;"><span><strong>שם הלקוח:</strong></span></td><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;"><span>{{clientName}}</span></td></tr>
      <tr><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;"><span><strong>טלפון:</strong></span></td><td style="padding: 8px; border-bottom: 1px solid #e2e8f0;"><span>{{clientPhone}}</span></td></tr>
      <tr><td style="padding: 8px;"><span><strong>תאריך:</strong></span></td><td style="padding: 8px;"><span>{{currentDate}}</span></td></tr>
    </table>
  </div>
  <h2>תנאי השירות</h2>
  <p>הכנס כאן את תנאי השירות שלך...</p>
  <div style="margin-top: 30px; padding: 20px; background: rgba(21, 97, 237, 0.06); border-radius: 8px;">
    <h4 style="color: #1561ED; margin-top: 0;">הצהרת הלקוח</h4>
    <p style="color: #333; margin-bottom: 0;">אני, {{clientName}}, מצהיר/ה כי קראתי והבנתי את כל תנאי הסכם זה ואני מסכים/ה להם.</p>
  </div>
</div>`;

/** מיפוי משתנים דינמיים לתוויות (עברית/אנגלית) – לשימוש בעורך אחרי יצירה עם AI */
const PLACEHOLDER_LABELS = {
  clientName: { labelHe: "שם לקוח", labelEn: "Client name" },
  clientPhone: { labelHe: "טלפון", labelEn: "Phone" },
  clientEmail: { labelHe: "אימייל", labelEn: "Email" },
  clientAddress: { labelHe: "כתובת", labelEn: "Address" },
  currentDate: { labelHe: "תאריך", labelEn: "Date" },
  companyName: { labelHe: "שם החברה", labelEn: "Company name" },
  serviceName: { labelHe: "שם השירות", labelEn: "Service name" },
  serviceDescription: { labelHe: "תיאור השירות", labelEn: "Service description" },
  price: { labelHe: "מחיר", labelEn: "Price" },
  currency: { labelHe: "מטבע", labelEn: "Currency" },
  paymentTerms: { labelHe: "תנאי תשלום", labelEn: "Payment terms" },
  duration: { labelHe: "משך", labelEn: "Duration" },
  projectName: { labelHe: "שם הפרויקט", labelEn: "Project name" },
  startDate: { labelHe: "תאריך התחלה", labelEn: "Start date" },
  endDate: { labelHe: "תאריך סיום", labelEn: "End date" },
};

const CompanyTemplates = () => {
  const { isRTL } = useApp();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [showAiWizard, setShowAiWizard] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [form, setForm] = useState({
    name: "",
    description: "",
    templateHtml: DEFAULT_HTML,
    contentHtml: null,
    pageBackgroundColor: null,
    documentDesignId: null,
    documentDesignColors: null,
    dynamicVariables: undefined,
  });
  const [designs, setDesigns] = useState([]);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (showModal) {
      getDocumentDesigns().then((data) => setDesigns(data?.designs || [])).catch(() => setDesigns([]));
    }
  }, [showModal]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getTemplates();
      setTemplates(data?.templates || []);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: "", description: "", templateHtml: DEFAULT_HTML, contentHtml: null, pageBackgroundColor: null, documentDesignId: null, documentDesignColors: null, dynamicVariables: undefined });
    setTab(0);
    setShowModal(true);
  };

  const handleAiGenerateSuccess = ({ templateHtml, suggestedName, suggestedDescription, placeholders = [], documentDesignId = null, documentDesignColors = null, contentHtml = null }) => {
    setEditingId(null);
    const dynamicVariables = placeholders.map((key) => {
      const labels = PLACEHOLDER_LABELS[key];
      return {
        key,
        labelEn: labels?.labelEn ?? key,
        labelHe: labels?.labelHe ?? key,
      };
    });
    const hasDesign = !!documentDesignId;
    setForm({
      name: suggestedName || "",
      description: suggestedDescription || "",
      templateHtml: templateHtml || DEFAULT_HTML,
      contentHtml: hasDesign ? (contentHtml || "") : null,
      pageBackgroundColor: null,
      documentDesignId: documentDesignId ?? null,
      documentDesignColors: documentDesignColors ?? null,
      dynamicVariables: dynamicVariables.length ? dynamicVariables : undefined,
    });
    setTab(0);
    setShowModal(true);
  };

  const openEdit = async (t) => {
    if (t.isGlobal) return;
    try {
      const full = await getTemplate(t._id);
      setEditingId(t._id);
      setForm({
        name: full.name || "",
        description: full.description || "",
        templateHtml: full.templateHtml || "",
        contentHtml: full.contentHtml ?? null,
        pageBackgroundColor: full.pageBackgroundColor ?? null,
        documentDesignId: full.documentDesignId ?? null,
        documentDesignColors: full.documentDesignColors ?? null,
        dynamicVariables: undefined,
      });
      setTab(0);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to load template:", err);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setTab(0);
  };

  const handlePreview = async () => {
    try {
      if (form.documentDesignId && form.contentHtml != null) {
        const data = await mergePreviewTemplate(form.documentDesignId, form.documentDesignColors || {}, form.contentHtml || "");
        setPreviewHtml(data?.html || "");
      } else {
        const data = await previewTemplate(form.templateHtml);
        setPreviewHtml(data?.html || "");
      }
      setTab(1);
    } catch (err) {
      console.error("Preview failed:", err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const hasDesign = !!form.documentDesignId;
    const hasContent = hasDesign ? (form.contentHtml != null && form.contentHtml.trim() !== "") : form.templateHtml?.trim();
    if (!form.name?.trim() || !hasContent) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateTemplate(editingId, {
          name: form.name.trim(),
          description: form.description.trim(),
          ...(hasDesign ? { contentHtml: form.contentHtml || "" } : { templateHtml: form.templateHtml }),
          pageBackgroundColor: form.pageBackgroundColor ?? null,
          documentDesignId: form.documentDesignId ?? null,
          documentDesignColors: form.documentDesignColors ?? null,
        });
      } else {
        await createTemplate({
          name: form.name.trim(),
          description: form.description.trim(),
          ...(hasDesign ? { contentHtml: form.contentHtml || "" } : { templateHtml: form.templateHtml }),
          pageBackgroundColor: form.pageBackgroundColor ?? null,
          documentDesignId: form.documentDesignId ?? null,
          documentDesignColors: form.documentDesignColors ?? null,
        });
      }
      closeModal();
      await loadTemplates();
    } catch (err) {
      console.error("Failed to save template:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (t.isGlobal) return;
    if (
      !window.confirm(
        isRTL ? `למחוק את התבנית "${t.name}"?` : `Delete template "${t.name}"?`
      )
    )
      return;
    try {
      await deleteTemplate(t._id);
      await loadTemplates();
    } catch (err) {
      console.error("Failed to delete template:", err);
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const isEditable = (t) => !t.isGlobal;

  return (
    <Box sx={{ pt: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          {isRTL ? "תבניות חוזים" : "Contract Templates"}
        </Typography>
        <Stack direction="row" sx={{ gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowAiWizard(true)}
            endIcon={
              <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 20, height: 20 } }}>
                <LuWandSparkles />
              </Box>
            }
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "#000",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#000" : "#fff",
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "#000",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
                borderColor: (theme) =>
                  theme.palette.mode === "dark" ? "#fff" : "#000",
              },
            }}
          >
            {isRTL ? "יצירה עם Ai" : "Generate with Ai"}
          </Button>
          <Button
            variant="contained"
            onClick={openCreate}
            endIcon={
              <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 20, height: 20 } }}>
                <PlusCircleIcon />
              </Box>
            }
          >
            {isRTL ? "צור תבנית" : "Create Template"}
          </Button>
        </Stack>
      </Stack>

      <AiGenerateWizard
        open={showAiWizard}
        onClose={() => setShowAiWizard(false)}
        onSuccess={handleAiGenerateSuccess}
        isRTL={isRTL}
      />

      {loading ? (
        <AppLoading fullPage />
      ) : templates.length === 0 ? (
        <Card variant="outlined">
          <CardContent >
            <Box py={6} textAlign="center">
              <Typography color="text.secondary" gutterBottom>
                {isRTL ? "אין תבניות" : "No templates"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isRTL
                  ? "צור תבנית חוזה HTML עם משתנים כדי לשלוח חוזים ללקוחות"
                  : "Create an HTML contract template with variables to send contracts to clients"}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? "התבניות שלך" : "Your Templates"}
            </Typography>
            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
              {templates.map((t) => (
                <Grid item xs={12} sm={6} md={4} key={t._id} sx={{ display: "flex",  width: 370 , minWidth: '32%'}}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 280,
                      minWidth: 0,
                    }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                      }}
                    >
                    <CardContent
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                      }}
                    >
                      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          gutterBottom
                        >
                          {t.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {t.description ||
                            (isRTL ? "ללא תיאור" : "No description")}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mb: 1 }}
                        >
                          {t.isGlobal && (
                            <Chip
                              label={isRTL ? "גלובלית" : "Global"}
                              size="small"
                              color="info"
                              sx={{ padding: '2px 4px 5px 4px', userSelect: 'none', pointerEvents: 'none' }}
                            />
                          )}
                          {t.isDefault && (
                            <Chip
                              label={isRTL ? "ברירת מחדל" : "Default"}
                              size="small"
                              sx={{ padding: '2px 4px 5px 4px', userSelect: 'none', pointerEvents: 'none' }}
                            />
                          )}
                          {!t.isGlobal && !t.isDefault && (
                            <Chip
                              label={isRTL ? "תבנית מותאמת" : "Custom Template"}
                              size="small"
                              variant="outlined"
                              sx={{ padding: '2px 4px 5px 4px', userSelect: 'none', pointerEvents: 'none' }}
                              
                            />
                          )}
                        </Stack>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        v{t.version || "1.0.0"} · {formatDate(t.createdAt)}
                      </Typography>
                      {isEditable(t) && (
                        <Stack direction="row" spacing={2} sx={{ mt: 1.5, gap: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openEdit(t)}
                            sx={{ height: 35, minHeight: 35 }}
                          >
                            {isRTL ? "ערוך" : "Edit"}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleDelete(t)}
                            sx={{ height: 35, minHeight: 35 }}
                          >
                            {isRTL ? "מחק" : "Delete"}
                          </Button>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={showModal}
        onClose={closeModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "96vw",
            maxWidth: 1600,
            height: "92vh",
            maxHeight: "92vh",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          {editingId
            ? isRTL
              ? "עריכת תבנית"
              : "Edit Template"
            : isRTL
            ? "צור תבנית חדשה"
            : "Create New Template"}
        </DialogTitle>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            if (v === 3) {
              handlePreview().then(() => setTab(3));
            } else {
              setTab(v);
            }
          }}
          sx={{ px: 2, "& .MuiTab-root": { textTransform: "none", fontSize: "0.875rem" } }}
        >
          <Tab label={isRTL ? "עימוד מסמך" : "Document layout"} />
          <Tab label={isRTL ? "עיצוב מסמך (צבעים)" : "Design (colors)"} />
          <Tab label={isRTL ? "מידע ותוכן במסמך" : "Content & data"} />
          <Tab label={isRTL ? "תצוגה מקדימה" : "Preview"} />
        </Tabs>
        {tab === 3 ? (
          <>
            <DialogContent
              dividers
              sx={{
                bgcolor: "grey.200",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  py: 0,
                  px: 0,
                  borderRadius: 1,
                  minHeight: 200,
                  width: "100%",
                  maxWidth: 800,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  backgroundColor: form.pageBackgroundColor ?? "#ffffff",
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(previewHtml),
                }}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "flex-end", flexDirection: "row", direction: "ltr" }}>
              <Button onClick={() => setTab(0)}>
                {isRTL ? "חזרה לעריכה" : "Back to Edit"}
              </Button>
            </DialogActions>
          </>
        ) : (
          <form
            onSubmit={handleSave}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
            }}
          >
            <DialogContent
              dividers
              sx={{
                "&.MuiDialogContent-root": {
                  width: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                },
              }}
            >
              {tab === 0 && (
                <Stack spacing={2} sx={{ width: "100%", flex: 1, minHeight: 0, overflow: "auto" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {isRTL ? "בחר עיצוב (עימוד) למסמך" : "Choose document layout"}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: "pointer",
                        border: !form.documentDesignId ? "2px solid" : "1px solid",
                        borderColor: !form.documentDesignId ? "primary.main" : "divider",
                        bgcolor: !form.documentDesignId ? "action.selected" : "background.paper",
                        width: 172,
                        minWidth: 172,
                        maxWidth: 172,
                        px: 1.25,
                        boxSizing: "border-box",
                      }}
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          documentDesignId: null,
                          documentDesignColors: null,
                          templateHtml: (f.documentDesignId && f.contentHtml != null) ? f.contentHtml : f.templateHtml,
                          contentHtml: null,
                        }));
                      }}
                    >
                      <CardContent sx={{ py: 2, px: 0, "&:last-child": { pb: 2 }, textAlign: "center" }}>
                        <Typography variant="body2" fontWeight={600}>
                          {isRTL ? "ללא עיצוב" : "No design"}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  {DESIGN_LAYOUT_CATEGORIES.map((category) => {
                    const categoryDesigns = designs.filter((d) => d.category === category.id);
                    if (categoryDesigns.length === 0) return null;
                    return (
                      <Box key={category.id} sx={{ mb: 2 }}>
                        <Typography variant="overline" sx={{ display: "block", mb: 0.75, color: "text.secondary", fontWeight: 600 }}>
                          {isRTL ? category.nameHe : category.nameEn}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          {categoryDesigns.map((d) => (
                            <Card
                              key={d.id}
                              variant="outlined"
                              sx={{
                                cursor: "pointer",
                                border: form.documentDesignId === d.id ? "2px solid" : "1px solid",
                                borderColor: form.documentDesignId === d.id ? "primary.main" : "divider",
                                bgcolor: form.documentDesignId === d.id ? "action.selected" : "background.paper",
                                width: 172,
                                minWidth: 172,
                                maxWidth: 172,
                                px: 1.25,
                                boxSizing: "border-box",
                              }}
                              onClick={() => {
                                const pal = COLOR_PALETTES[0];
                                setForm((f) => ({
                                  ...f,
                                  documentDesignId: d.id,
                                  documentDesignColors: pal ? { primary: pal.primary, secondary: pal.secondary, accent: pal.accent, headerBg: pal.headerBg, textOnHeader: pal.textOnHeader } : null,
                                  contentHtml: f.contentHtml != null ? f.contentHtml : f.templateHtml || "",
                                }));
                              }}
                            >
                              <CardContent sx={{ py: 1.5, px: 0, "&:last-child": { pb: 1.5 }, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Box sx={{ width: 152, height: 95, mb: 0.75, flexShrink: 0 }}>
                                  <DocumentDesignThumbnail designId={d.id} sx={{ width: 152, height: 95, minWidth: 152, minHeight: 95, maxWidth: 152, maxHeight: 95 }} />
                                </Box>
                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "100%", fontSize: "0.8125rem" }}>
                                  {isRTL ? d.nameHe : d.nameEn}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                  {(() => {
                    const knownIds = new Set(DESIGN_LAYOUT_CATEGORIES.map((c) => c.id));
                    const otherDesigns = designs.filter((d) => !knownIds.has(d.category));
                    if (otherDesigns.length === 0) return null;
                    return (
                      <Box key="other" sx={{ mb: 2 }}>
                        <Typography variant="overline" sx={{ display: "block", mb: 0.75, color: "text.secondary", fontWeight: 600 }}>
                          {isRTL ? "אחר" : "Other"}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          {otherDesigns.map((d) => (
                            <Card
                              key={d.id}
                              variant="outlined"
                              sx={{
                                cursor: "pointer",
                                border: form.documentDesignId === d.id ? "2px solid" : "1px solid",
                                borderColor: form.documentDesignId === d.id ? "primary.main" : "divider",
                                bgcolor: form.documentDesignId === d.id ? "action.selected" : "background.paper",
                                width: 172,
                                minWidth: 172,
                                maxWidth: 172,
                                px: 1.25,
                                boxSizing: "border-box",
                              }}
                              onClick={() => {
                                const pal = COLOR_PALETTES[0];
                                setForm((f) => ({
                                  ...f,
                                  documentDesignId: d.id,
                                  documentDesignColors: pal ? { primary: pal.primary, secondary: pal.secondary, accent: pal.accent, headerBg: pal.headerBg, textOnHeader: pal.textOnHeader } : null,
                                  contentHtml: f.contentHtml != null ? f.contentHtml : f.templateHtml || "",
                                }));
                              }}
                            >
                              <CardContent sx={{ py: 1.5, px: 0, "&:last-child": { pb: 1.5 }, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Box sx={{ width: 152, height: 95, mb: 0.75, flexShrink: 0 }}>
                                  <DocumentDesignThumbnail designId={d.id} sx={{ width: 152, height: 95, minWidth: 152, minHeight: 95, maxWidth: 152, maxHeight: 95 }} />
                                </Box>
                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "100%", fontSize: "0.8125rem" }}>
                                  {isRTL ? d.nameHe : d.nameEn}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
              {tab === 1 && (
                <Stack spacing={2} sx={{ width: "100%", flex: 1, minHeight: 0, overflow: "auto" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {isRTL ? "עימוד שנבחר" : "Selected layout"}
                    </Typography>
                    {form.documentDesignId ? (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        <DocumentDesignThumbnail
                          designId={form.documentDesignId}
                          colors={form.documentDesignColors ? { primary: form.documentDesignColors.primary, secondary: form.documentDesignColors.secondary, accent: form.documentDesignColors.accent, headerBg: form.documentDesignColors.headerBg, textOnHeader: form.documentDesignColors.textOnHeader } : undefined}
                          sx={{ width: 280, height: 175, minWidth: 280, minHeight: 175, maxWidth: 280, maxHeight: 175 }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                          {isRTL ? (designs.find((d) => d.id === form.documentDesignId)?.nameHe ?? form.documentDesignId) : (designs.find((d) => d.id === form.documentDesignId)?.nameEn ?? form.documentDesignId)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {isRTL ? "בחר עימוד בלשונית עימוד מסמך" : "Choose a layout in the Document layout tab"}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {isRTL ? "בחר פלטת צבעים" : "Choose color palette"}
                  </Typography>
                  {COLOR_PALETTE_CATEGORIES.map((category) => (
                    <Box key={category.id} sx={{ mb: 2 }}>
                      <Typography variant="overline" sx={{ display: "block", mb: 0.75, color: "text.secondary", fontWeight: 600 }}>
                        {isRTL ? category.nameHe : category.nameEn}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {category.palettes.map((pal) => {
                          const isSelected = form.documentDesignColors?.primary === pal.primary;
                          return (
                            <Card
                              key={pal.id}
                              variant="outlined"
                              sx={{
                                cursor: "pointer",
                                border: isSelected ? "2px solid" : "1px solid",
                                borderColor: isSelected ? "primary.main" : "divider",
                                width: 90,
                                minWidth: 90,
                                maxWidth: 90,
                                height: 80,
                                minHeight: 80,
                                maxHeight: 80,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                borderRadius: 1.25,
                              }}
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  documentDesignColors: { primary: pal.primary, secondary: pal.secondary, accent: pal.accent, headerBg: pal.headerBg, textOnHeader: pal.textOnHeader },
                                }))
                              }
                            >
                              <Box sx={{ display: "flex", gap: 0.5, flex: 1, minHeight: 0, p: 0.75, alignItems: "stretch" }}>
                                <Box sx={{ flex: 1, borderRadius: 0.5, bgcolor: pal.primary }} />
                                <Box sx={{ flex: 1, borderRadius: 0.5, bgcolor: pal.secondary }} />
                                <Box sx={{ flex: 1, borderRadius: 0.5, bgcolor: pal.accent }} />
                              </Box>
                              <CardContent sx={{ py: 0.25, px: 0.5, "&:last-child": { pb: 0.25 }, textAlign: "center" }}>
                                <Typography variant="caption" fontSize="0.7rem" fontWeight={600} noWrap sx={{ lineHeight: 1.2 }}>
                                  {isRTL ? pal.nameHe : pal.nameEn}
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
              {tab === 2 && (
                <Stack
                  spacing={2}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                      width: "100%",
                      flexShrink: 0,
                    }}
                  >
                    <TextField
                      label={isRTL ? "שם התבנית" : "Template name"}
                      required
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                      placeholder={
                        isRTL ? "לדוגמה: הסכם שירות" : "e.g. Service Agreement"
                      }
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={isRTL ? "תיאור" : "Description"}
                      value={form.description}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, description: e.target.value }))
                      }
                      placeholder={isRTL ? "תיאור קצר" : "Short description"}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {form.documentDesignId ? (isRTL ? "תוכן המסמך (טקסט ונתונים)" : "Document content (text & data)") : (isRTL ? "תוכן התבנית" : "Template content")}
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 0, width: "100%", minWidth: 0 }}>
                    <TemplateRichEditor
                      value={form.documentDesignId ? (form.contentHtml ?? "") : form.templateHtml}
                      onChange={(html) =>
                        setForm((f) =>
                          f.documentDesignId ? { ...f, contentHtml: html || "" } : { ...f, templateHtml: html || "" }
                        )
                      }
                      isRTL={isRTL}
                      minHeight={320}
                      pageBackgroundColor={form.pageBackgroundColor}
                      onPageBackgroundChange={(color) =>
                        setForm((f) => ({ ...f, pageBackgroundColor: color }))
                      }
                      dynamicVariables={form.dynamicVariables}
                    />
                  </Box>
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "flex-end", flexDirection: "row", direction: "ltr" }}>
              <Button onClick={closeModal}>{isRTL ? "ביטול" : "Cancel"}</Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving
                  ? isRTL
                    ? "שומר..."
                    : "Saving..."
                  : editingId
                  ? isRTL
                    ? "שמור שינויים"
                    : "Save Changes"
                  : isRTL
                  ? "שמור תבנית"
                  : "Save Template"}
              </Button>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </Box>
  );
};

export default CompanyTemplates;
